import React, { useState, useEffect, useCallback } from 'react';
import { Search, Package, Calendar, FileText, AlertCircle, User, FileCheck, ChevronDown, ChevronRight, Hash, Layers, ScanBarcode, Keyboard, Camera, X } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { mediaAttachmentService } from '../../../../services/mediaAttachmentService';
import { MediaAttachmentDto } from '../../../../types/mediaAttachment';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import {
  CompletedPickListDto,
  CompletedPickListDetailDto,
  CompletedPickListLineDto,
  PListOrderDto
} from '../../../../types/completedPickingList';
import { Card } from '../../../common/Card';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';

type TabType = 'lines' | 'packages' | 'info';

interface CompletedPickingListsProps {
  onBack?: () => void;
}

export const CompletedPickingLists: React.FC<CompletedPickingListsProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const { getDisplayName } = useOperatorDisplayNames();

  // State for master list
  const [pickLists, setPickLists] = useState<CompletedPickListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // State for detail view
  const [selectedPListId, setSelectedPListId] = useState<number | null>(null);
  const [detailData, setDetailData] = useState<CompletedPickListDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('lines');

  // Signaturbilder til Info-fanen (36334240): dataURL-ene ligger ikke i listedataene —
  // hentes on-demand fra plukkdetalj-headeren når fanen åpnes for en signert liste.
  const [signatureView, setSignatureView] = useState<{
    pListId: number;
    signature1?: string | null; signature1DateTime?: string | null; signature1Location?: string | null;
    signature2?: string | null; signature2DateTime?: string | null; signature2Location?: string | null;
  } | null>(null);
  const [signatureLoading, setSignatureLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'info' || !selectedPListId) return;
    const current = pickLists.find(p => p.pListID === selectedPListId);
    if (!current || (current.hasSignature1 !== 1 && current.hasSignature2 !== 1)) {
      setSignatureView(null);
      return;
    }
    if (signatureView?.pListId === selectedPListId) return;

    let cancelled = false;
    setSignatureLoading(true);
    pickingService.getPickingDetails(selectedPListId)
      .then(d => {
        if (cancelled) return;
        setSignatureView({
          pListId: selectedPListId,
          signature1: d.header.signature1,
          signature1DateTime: d.header.signature1DateTime,
          signature1Location: d.header.signature1Location,
          signature2: d.header.signature2,
          signature2DateTime: d.header.signature2DateTime,
          signature2Location: d.header.signature2Location
        });
      })
      .catch(err => {
        console.error('Could not load signature images:', err);
        if (!cancelled) setSignatureView(null);
      })
      .finally(() => { if (!cancelled) setSignatureLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, selectedPListId, pickLists, signatureView?.pListId]);

  // Foto av godset (36334240): bilder tatt ved utlevering/plukk ligger som
  // MediaAttachment (PList/{pListID}) — hentes on-demand når Info-fanen åpnes
  // og vises under signaturen. Stille feil: seksjonen utelates bare.
  const [photoView, setPhotoView] = useState<{ pListId: number; photos: MediaAttachmentDto[] } | null>(null);
  const [photoLightbox, setPhotoLightbox] = useState<MediaAttachmentDto | null>(null);

  useEffect(() => {
    if (activeTab !== 'info' || !selectedPListId) return;
    if (photoView?.pListId === selectedPListId) return;

    let cancelled = false;
    mediaAttachmentService.getAttachments('PList', String(selectedPListId))
      .then(attachments => {
        if (cancelled) return;
        setPhotoView({
          pListId: selectedPListId,
          photos: attachments.filter(a => a.mediaType === 'photo' && a.dataUrl)
        });
      })
      .catch(err => {
        console.warn('Could not load photo attachments:', err);
        if (!cancelled) setPhotoView({ pListId: selectedPListId, photos: [] });
      });
    return () => { cancelled = true; };
  }, [activeTab, selectedPListId, photoView?.pListId]);
  // Serial/batch detail rows are open by default; this tracks the ones the user manually collapsed.
  const [collapsedSerialBatch, setCollapsedSerialBatch] = useState<Set<string>>(new Set());

  // Initialize date filters (default 7 days back)
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    setDateTo(today.toISOString().split('T')[0]);
    setDateFrom(sevenDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Load completed pick lists
  const loadPickLists = useCallback(async () => {
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setError(null);

      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // Include entire day

      const lists = await pickingService.getCompletedPickLists(fromDate, toDate, currentWarehouse || undefined);
      setPickLists(lists);
    } catch (err) {
      const error = err as Error;
      setError(error.message || t('warehouse.modules.history.completedPickingLists.errors.loadFailed'));
      console.error('Error loading completed pick lists:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Load pick lists when dates change
  useEffect(() => {
    if (dateFrom && dateTo) {
      loadPickLists();
    }
  }, [dateFrom, dateTo, loadPickLists]);

  // Load detail data when a pick list is selected
  const loadDetailData = useCallback(async (pListId: number) => {
    try {
      setDetailLoading(true);
      setDetailError(null);

      const detail = await pickingService.getCompletedPickListDetail(pListId);

      if (!detail) {
        setDetailError(t('warehouse.modules.history.completedPickingLists.errors.notFound'));
        return;
      }

      setDetailData(detail);
    } catch (err) {
      const error = err as Error;
      setDetailError(error.message || t('warehouse.modules.history.completedPickingLists.errors.loadDetailsFailed'));
      console.error('Error loading pick list detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Handle pick list selection
  const handleSelectPickList = useCallback((pListId: number) => {
    setSelectedPListId(pListId);
    setActiveTab('lines'); // Reset to first tab
    setCollapsedSerialBatch(new Set()); // Serial/batch rows open by default for new selection
    loadDetailData(pListId);
  }, [loadDetailData]);

  // Filter pick lists based on search term
  const filteredPickLists = pickLists.filter(list => {
    const searchLower = searchTerm.toLowerCase();
    return (
      list.pListID.toString().includes(searchLower) ||
      list.description1?.toLowerCase().includes(searchLower) ||
      list.description2?.toLowerCase().includes(searchLower) ||
      list.operatorID?.toLowerCase().includes(searchLower) ||
      list.carrierName?.toLowerCase().includes(searchLower)
    );
  });

  // Render master list (left side)
  const renderMasterList = () => (
    <div className="flex flex-col h-full">
      {/* Search box */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder={t('warehouse.modules.history.completedPickingLists.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* List of pick lists */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
            <span className="ml-2 text-sm text-gray-600">{t('common.status.loading')}</span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {!loading && !error && filteredPickLists.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Package size={48} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">{t('warehouse.modules.history.completedPickingLists.noListsFound')}</p>
          </div>
        )}

        {!loading && !error && filteredPickLists.map((list) => (
          <Card
            key={list.pListID}
            onClick={() => handleSelectPickList(list.pListID)}
            className={`p-3 transition-all ${
              selectedPListId === list.pListID
                ? 'border-blue-500 border-2 bg-blue-50'
                : 'hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileCheck size={16} className="text-green-600 flex-shrink-0" />
                  <span className="font-semibold text-gray-900 truncate" title={list.description2 || undefined}>
                    {list.description2 || `PL ${list.pListID}`}
                  </span>
                  {(list.hasSignature1 === 1 || list.hasSignature2 === 1) && (
                    <span title={t('warehouse.modules.history.completedPickingLists.hasSignature')}>
                      <FileText size={14} className="text-blue-600" />
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-600 space-y-0.5">
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-gray-700">PL {list.pListID}</span>
                    <span className="text-gray-400">•</span>
                    <Calendar size={12} className="flex-shrink-0" />
                    <span>{pickingService.formatDate(list.deliveryDate || list.pListDate)}</span>
                  </div>

                  {list.operatorID && (
                    <div className="flex items-center gap-1">
                      <User size={12} className="flex-shrink-0" />
                      <span>{getDisplayName(list.operatorID)}</span>
                    </div>
                  )}

                  {list.description1 && (
                    <div className="truncate text-gray-500" title={list.description1}>
                      {list.description1}
                    </div>
                  )}
                </div>
              </div>

              {list.numOfOrderLines && list.numOfOrderLines > 0 && (
                <div className="ml-2 flex-shrink-0">
                  <div className="text-xs font-medium text-gray-600">
                    {list.numOfOrderLines} {t('warehouse.modules.history.completedPickingLists.lines')}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // Render detail view (right side)
  const renderDetailView = () => {
    if (!selectedPListId) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          <div className="text-center">
            <Package size={64} className="mx-auto mb-4 text-gray-300" />
            <p>{t('warehouse.modules.history.completedPickingLists.selectListToViewDetails')}</p>
          </div>
        </div>
      );
    }

    if (detailLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="ml-3 text-gray-600">{t('warehouse.modules.history.completedPickingLists.loadingDetails')}</span>
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-red-600">
            <AlertCircle size={48} className="mx-auto mb-4" />
            <p>{detailError}</p>
          </div>
        </div>
      );
    }

    if (!detailData) {
      return null;
    }

    const { pickList, orderLines, packages } = detailData;

    // Distinct order numbers on this pick list (ERP + internal). Usually one, but a pick list can span several.
    const orderNumbers = Array.from(
      new Map(
        orderLines.map((l) => [`${l.orderNo}|${l.orderNoERP ?? ''}`, { orderNo: l.orderNo, orderNoERP: l.orderNoERP }])
      ).values()
    );
    const hasMultipleOrders = orderNumbers.length > 1;
    // Distinct ERP order numbers for the header and Info tab — covers all orders, not just PList.Description2.
    // Deduped by ERP value: several internal orders can share one ERP order number (e.g. SX001).
    const erpOrderLabel = Array.from(
      new Set(orderNumbers.map((o) => o.orderNoERP || `${o.orderNo}`))
    ).join(', ');

    return (
      <div className="flex flex-col h-full">
        {/* Header info */}
        <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {t('warehouse.modules.history.completedPickingLists.pickingList')} {pickList.pListID}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            {erpOrderLabel && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.orderNumber')}:</span>
                <span className="ml-2 font-medium">{erpOrderLabel}</span>
              </div>
            )}

            <div>
              <span className="text-gray-500">{t('common.fields.status')}:</span>
              <span className="ml-2"><StatusBadge value={pickList.pListStatus} statusType={1} /></span>
            </div>

            {pickList.pListDate && (
              <div>
                <span className="text-gray-500">{t('common.fields.date')}:</span>
                <span className="ml-2 font-medium">
                  {pickingService.formatDateTime(pickList.pListDate)}
                </span>
              </div>
            )}

            {pickList.operatorID && (
              <div>
                <span className="text-gray-500">{t('warehouse.general.operator')}:</span>
                <span className="ml-2 font-medium">{getDisplayName(pickList.operatorID)}</span>
              </div>
            )}

            {pickList.deliveryDate && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.deliveryDate')}:</span>
                <span className="ml-2 font-medium">
                  {pickingService.formatDate(pickList.deliveryDate)}
                </span>
              </div>
            )}

            {pickList.carrierName && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.carrier')}:</span>
                <span className="ml-2 font-medium">{pickList.carrierName}</span>
              </div>
            )}

            {pickList.numOfPackages !== undefined && pickList.numOfPackages > 0 && (
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.numberOfPackages')}:</span>
                <span className="ml-2 font-medium">{pickList.numOfPackages}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('lines')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'lines'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('warehouse.modules.history.completedPickingLists.orderLines')} ({orderLines.length})
          </button>
          <button
            onClick={() => setActiveTab('packages')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'packages'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('warehouse.modules.history.completedPickingLists.packages')} ({packages.length})
          </button>
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'info'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('warehouse.modules.history.completedPickingLists.info')}
          </button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'lines' && renderOrderLinesTab(orderLines, hasMultipleOrders)}
          {activeTab === 'packages' && renderPackagesTab(packages)}
          {activeTab === 'info' && renderInfoTab(pickList, erpOrderLabel)}
        </div>
      </div>
    );
  };

  // Render order lines tab. When the pick list spans several orders, an "ERP order" column is shown
  // so each line can be tied to its order (line numbers can repeat across orders).
  const renderOrderLinesTab = (lines: CompletedPickListLineDto[], showErpOrderColumn: boolean) => {
    if (lines.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <FileText size={48} className="mx-auto mb-2 text-gray-300" />
          <p>{t('warehouse.modules.history.completedPickingLists.noOrderLinesFound')}</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-2 py-2 w-8"></th>
              {showErpOrderColumn && (
                <th className="px-3 py-2 text-right font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.erpOrderNo')}</th>
              )}
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.orderLineNo')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.general.itemNumber')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.itemDescription', {}, 'Varebeskrivelse')}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.quantityOrdered')}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.quantityPicked')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.fromLocation')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.pickedBy')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('warehouse.modules.history.completedPickingLists.entryMethod', {}, 'Registrering')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {lines.map((line, index) => {
              const rowKey = `${line.orderNo}-${line.lineNo}-${index}`;
              const serials = line.serialNumbers ?? [];
              const batches = line.batches ?? [];
              const hasSerialBatch = serials.length > 0 || batches.length > 0;
              const isExpanded = hasSerialBatch && !collapsedSerialBatch.has(rowKey);

              const toggleExpand = () => {
                setCollapsedSerialBatch((prev) => {
                  const next = new Set(prev);
                  if (next.has(rowKey)) next.delete(rowKey);
                  else next.add(rowKey);
                  return next;
                });
              };

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={`hover:bg-gray-50 ${hasSerialBatch ? 'cursor-pointer' : ''}`}
                    onClick={hasSerialBatch ? toggleExpand : undefined}
                  >
                    <td className="px-2 py-2 text-gray-400">
                      {hasSerialBatch && (
                        isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                      )}
                    </td>
                    {showErpOrderColumn && (
                      <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                        {line.orderNoERP ?? line.orderNo ?? '-'}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{line.lineNoERP ?? line.lineNo ?? '-'}</span>
                      {line.lineNoERP != null && line.lineNo != null && (
                        <span className="ml-1 text-xs text-gray-400">({line.lineNo})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-900">{line.itemNo}</td>
                    <td className="px-3 py-2 text-gray-700">
                      <div className="max-w-xs truncate" title={line.description1 || undefined}>
                        {line.description1 || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {line.quantity ?? '-'}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                      {line.pListLineQtyPicked ?? line.quantityPicked ?? '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {line.pickLocations && line.pickLocations.length > 0
                        ? line.pickLocations.join(', ')
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{getDisplayName(line.pickedBy) || '-'}</td>
                    <td className="px-3 py-2">
                      {/* 36334078: skann vs. manuell. «Skannet» = registrert via skannekanal
                          (tidsheuristikk på keyboard wedge) — sterk indikasjon, ikke bevis. */}
                      {line.pickEntryMethod === 1 ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                          title={`${t('warehouse.modules.history.completedPickingLists.entryMethodScannedHint', {}, 'Tempo-verifisert skann (strekkode lest av skanner)')}${line.scannedBarcode ? ` — ${line.scannedBarcode}` : ''}`}
                        >
                          <ScanBarcode size={13} />
                          {t('warehouse.modules.history.completedPickingLists.entryMethodScanned', {}, 'Skannet')}
                        </span>
                      ) : line.pickEntryMethod === 2 ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title={t('warehouse.modules.history.completedPickingLists.entryMethodManualHint', {}, 'Tastet eller redigert manuelt (eller blandet skann/manuell)')}
                        >
                          <Keyboard size={13} />
                          {t('warehouse.modules.history.completedPickingLists.entryMethodManual', {}, 'Manuell')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-gray-50/70">
                      <td></td>
                      <td colSpan={showErpOrderColumn ? 9 : 8} className="px-3 pb-3 pt-1">
                        <div className="flex flex-col gap-2">
                          {serials.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700">
                                <Hash size={13} />
                                {t('warehouse.modules.history.completedPickingLists.serialNumbers')}:
                              </span>
                              {serials.map((sn, i) => (
                                <span
                                  key={`${rowKey}-sn-${i}`}
                                  className="inline-flex items-center rounded-md bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800"
                                >
                                  {sn}
                                </span>
                              ))}
                            </div>
                          )}
                          {batches.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700">
                                <Layers size={13} />
                                {t('warehouse.modules.history.completedPickingLists.batches')}:
                              </span>
                              {batches.map((b, i) => (
                                <span
                                  key={`${rowKey}-batch-${i}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800"
                                >
                                  {b.batchNo}
                                  {b.quantity > 0 && (
                                    <span className="text-orange-600">× {b.quantity}</span>
                                  )}
                                  {b.expiryDate && (
                                    <span className="inline-flex items-center gap-0.5 text-orange-600" title={t('warehouse.modules.history.completedPickingLists.expiryDate')}>
                                      <Calendar size={11} />
                                      {pickingService.formatDate(b.expiryDate)}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render packages tab
  const renderPackagesTab = (packages: PListOrderDto[]) => {
    if (packages.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Package size={48} className="mx-auto mb-2 text-gray-300" />
          <p>{t('warehouse.modules.history.completedPickingLists.noPackageInfoFound')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {packages.map((pkg, index) => (
          <Card key={`${pkg.pListID}-${pkg.orderNo}-${index}`} className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              <div>
                <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.orderNumber')}:</span>
                <span className="ml-2 font-medium">{pkg.orderNo}</span>
              </div>

              {pkg.packageNo !== undefined && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.packageNumber')}:</span>
                  <span className="ml-2 font-medium">{pkg.packageNo}</span>
                </div>
              )}

              {pkg.numOfPackages !== undefined && pkg.numOfPackages > 0 && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.numberOfPackages')}:</span>
                  <span className="ml-2 font-medium">{pkg.numOfPackages}</span>
                </div>
              )}

              {pkg.totalWeight !== undefined && pkg.totalWeight > 0 && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.weight')}:</span>
                  <span className="ml-2 font-medium">{pkg.totalWeight.toFixed(2)} kg</span>
                </div>
              )}

              {pkg.totalVolume !== undefined && pkg.totalVolume > 0 && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.volume')}:</span>
                  <span className="ml-2 font-medium">{pkg.totalVolume.toFixed(2)} dm³</span>
                </div>
              )}

              {pkg.carrierName && (
                <div>
                  <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.carrier')}:</span>
                  <span className="ml-2 font-medium">{pkg.carrierName}</span>
                </div>
              )}

              {pkg.signature1DateTime && (
                <div className="col-span-2 md:col-span-3">
                  <div className="flex items-start gap-2 p-2 bg-green-50 rounded border border-green-200">
                    <FileCheck size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('warehouse.modules.history.completedPickingLists.signature1')}</div>
                      <div className="text-xs text-green-700">
                        {pickingService.formatDateTime(pkg.signature1DateTime)}
                        {pkg.signature1Location && ` - ${pkg.signature1Location}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {pkg.signature2DateTime && (
                <div className="col-span-2 md:col-span-3">
                  <div className="flex items-start gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <FileCheck size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-blue-900">{t('warehouse.modules.history.completedPickingLists.signature2')}</div>
                      <div className="text-xs text-blue-700">
                        {pickingService.formatDateTime(pkg.signature2DateTime)}
                        {pkg.signature2Location && ` - ${pkg.signature2Location}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  // Render info tab. erpOrderLabel holds the actual ERP order number(s) derived from the order lines,
  // which is correct for multi-order pick lists (PList.Description2 only holds the first order's reference).
  const renderInfoTab = (pickList: CompletedPickListDto, erpOrderLabel: string) => (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('warehouse.modules.history.completedPickingLists.pickingListInformation')}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        <div>
          <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.pickingListId')}:</span>
          <span className="ml-2 font-medium">{pickList.pListID}</span>
        </div>

        <div>
          <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.companyNumber')}:</span>
          <span className="ml-2 font-medium">{pickList.companyNo}</span>
        </div>

        {pickList.description1 && (
          <div className="md:col-span-2">
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.description1')}:</span>
            <span className="ml-2 font-medium">{pickList.description1}</span>
          </div>
        )}

        {erpOrderLabel && (
          <div className="md:col-span-2">
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.erpOrderNo')}:</span>
            <span className="ml-2 font-medium">{erpOrderLabel}</span>
          </div>
        )}

        <div>
          <span className="text-gray-500">Status:</span>
          <span className="ml-2"><StatusBadge value={pickList.pListStatus} statusType={1} /></span>
        </div>

        {pickList.priority !== undefined && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.priority')}:</span>
            <span className="ml-2 font-medium">{pickList.priority}</span>
          </div>
        )}

        {pickList.startWorkDateTime && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.startWork')}:</span>
            <span className="ml-2 font-medium">
              {pickingService.formatDateTime(pickList.startWorkDateTime)}
            </span>
          </div>
        )}

        {pickList.stopWorkDateTime && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.stopWork')}:</span>
            <span className="ml-2 font-medium">
              {pickingService.formatDateTime(pickList.stopWorkDateTime)}
            </span>
          </div>
        )}

        {pickList.deliveryMethodNo !== undefined && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.deliveryMethod')}:</span>
            <span className="ml-2 font-medium">{pickList.deliveryMethodNo}</span>
          </div>
        )}

        {pickList.packZoneID && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.packZone')}:</span>
            <span className="ml-2 font-medium">{pickList.packZoneID}</span>
          </div>
        )}

        {pickList.packageRemark && (
          <div className="md:col-span-2">
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.packageRemarks')}:</span>
            <span className="ml-2 font-medium">{pickList.packageRemark}</span>
          </div>
        )}

        {pickList.numOfOrderLinesPicked !== undefined && pickList.numOfOrderLines !== undefined && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.linesPicked')}:</span>
            <span className="ml-2 font-medium">
              {pickList.numOfOrderLinesPicked} / {pickList.numOfOrderLines}
            </span>
          </div>
        )}

        {pickList.numOfPallets !== undefined && pickList.numOfPallets > 0 && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.numberOfPallets')}:</span>
            <span className="ml-2 font-medium">{pickList.numOfPallets}</span>
          </div>
        )}

        {pickList.totalWeight !== undefined && pickList.totalWeight > 0 && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.totalWeight')}:</span>
            <span className="ml-2 font-medium">{pickList.totalWeight.toFixed(2)} kg</span>
          </div>
        )}

        {pickList.totalVolume !== undefined && pickList.totalVolume > 0 && (
          <div>
            <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.totalVolume')}:</span>
            <span className="ml-2 font-medium">{pickList.totalVolume.toFixed(2)} m³</span>
          </div>
        )}

        <div>
          <span className="text-gray-500">{t('warehouse.modules.history.completedPickingLists.signatures')}:</span>
          <span className="ml-2">
            {pickList.hasSignature1 === 1 && (
              <span className="inline-flex items-center gap-1 text-green-600">
                <FileCheck size={14} /> {t('warehouse.modules.history.completedPickingLists.signature1')}
              </span>
            )}
            {pickList.hasSignature1 === 1 && pickList.hasSignature2 === 1 && (
              <span className="mx-1 text-gray-400">|</span>
            )}
            {pickList.hasSignature2 === 1 && (
              <span className="inline-flex items-center gap-1 text-blue-600">
                <FileCheck size={14} /> {t('warehouse.modules.history.completedPickingLists.signature2')}
              </span>
            )}
            {pickList.hasSignature1 === 0 && pickList.hasSignature2 === 0 && (
              <span className="text-gray-400">{t('warehouse.modules.history.completedPickingLists.noSignatures')}</span>
            )}
          </span>
        </div>

        {pickList.createdBy && (
          <div>
            <span className="text-gray-500">{t('common.fields.createdBy')}:</span>
            <span className="ml-2 font-medium">{getDisplayName(pickList.createdBy)}</span>
          </div>
        )}

        {pickList.created && (
          <div>
            <span className="text-gray-500">{t('common.fields.createdAt')}:</span>
            <span className="ml-2 font-medium">
              {pickingService.formatDateTime(pickList.created)}
            </span>
          </div>
        )}
      </div>

      {/* Signaturbilder (36334240): hentes on-demand via getPickingDetails når Info-fanen
          åpnes — dataURL-ene er for tunge for listespørringen. 1899-datoer (Delphi-null)
          vises ikke som tidspunkt. */}
      {(pickList.hasSignature1 === 1 || pickList.hasSignature2 === 1) && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          {signatureLoading && (
            <div className="text-sm text-gray-400">{t('warehouse.modules.history.completedPickingLists.signatureLoading', {}, 'Henter signatur...')}</div>
          )}
          {!signatureLoading && signatureView?.pListId === pickList.pListID && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: t('warehouse.modules.history.completedPickingLists.signature1'), data: signatureView.signature1, at: signatureView.signature1DateTime, loc: signatureView.signature1Location },
                { label: t('warehouse.modules.history.completedPickingLists.signature2'), data: signatureView.signature2, at: signatureView.signature2DateTime, loc: signatureView.signature2Location }
              ].filter(s => s.data && s.data.startsWith('data:image')).map((s, idx) => (
                <div key={idx}>
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    <FileCheck size={14} className="text-green-600" />
                    <span className="font-medium text-gray-700">{s.label}</span>
                    {s.at && new Date(s.at).getFullYear() >= 1900 && (
                      <span className="text-gray-500">{pickingService.formatDateTime(s.at)}</span>
                    )}
                    {s.loc && <span className="text-gray-400">· {s.loc}</span>}
                  </div>
                  <img
                    src={s.data!}
                    alt={s.label}
                    className="border border-gray-200 rounded-lg bg-white max-h-40 w-auto max-w-full"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Foto av godset (36334240): bilder fra utlevering/plukk, under signaturen */}
      {photoView?.pListId === pickList.pListID && photoView.photos.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-sm">
            <Camera size={14} className="text-teal-600" />
            <span className="font-medium text-gray-700">
              {t('warehouse.modules.history.completedPickingLists.photos', { '0': `${photoView.photos.length}` }, `Bilder (${photoView.photos.length})`)}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {photoView.photos.map(photo => (
              <button
                key={photo.id}
                onClick={() => setPhotoLightbox(photo)}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:border-teal-400 transition-colors"
                title={photo.description || photo.fileName}
              >
                <img src={photo.dataUrl!} alt={photo.description || photo.fileName} className="h-24 w-24 object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox for godsbilder — skalerer opp til ~80 % av skjermen */}
      {photoLightbox && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setPhotoLightbox(null)}
        >
          <div className="relative w-[80vw] h-[80vh] flex flex-col">
            <button
              onClick={() => setPhotoLightbox(null)}
              className="absolute -top-3 -right-3 z-10 bg-white rounded-full p-2 shadow-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('common.buttons.close', {}, 'Lukk')}
            >
              <X size={20} />
            </button>
            <img
              src={photoLightbox.dataUrl!}
              alt={photoLightbox.description || photoLightbox.fileName}
              className="flex-1 min-h-0 w-full object-contain rounded-lg bg-white"
            />
            <div className="text-center text-white text-sm mt-2">
              {photoLightbox.description || photoLightbox.fileName}
              {photoLightbox.created && ` · ${pickingService.formatDateTime(photoLightbox.created)}`}
            </div>
          </div>
        </div>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header with filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BackButton onClick={onBack || (() => {})} />
            <h1 className="text-2xl font-bold text-gray-900">{t('warehouse.modules.history.completedPickingLists.title')}</h1>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.history.completedPickingLists.fromDate')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.modules.history.completedPickingLists.toDate')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={loadPickLists}
                disabled={loading || !dateFrom || !dateTo}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {t('common.buttons.search')}
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {t('warehouse.modules.history.completedPickingLists.dateFilterInfo')}
          </p>
        </div>

        {/* Master-Detail Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Master List (~25% on desktop) */}
          <div className="md:col-span-3">
            <Card className="p-4 h-[calc(100vh-16rem)]">
              {renderMasterList()}
            </Card>
          </div>

          {/* Detail View (~75% on desktop) */}
          <div className="md:col-span-9">
            <Card className="p-4 h-[calc(100vh-16rem)]">
              {renderDetailView()}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
